// Week 10 / Day 3 — World Light: Measurements, Maps and Route (bilingual RU/EN inline).
// Quest-style, playful review of Weeks 3–4. Educational only — fictional nims and meters.

export const maxWeek10Day3 = {
  id: 'max-week-10-day-3',
  studentId: 'max',
  weekId: 'week-10',
  dayId: 'day-3',
  title: 'Огонёк мира: измерения, карты и маршрут / World Light: Measurements, Maps and Route',
  shortTitle: 'Огонёк мира / World Light',
  theme:
    'Нимни вспоминает: мир можно измерять, сравнивать и находить путь по карте. / Nimny remembers: the world can be measured, compared and explored with a map.',
  estimatedTimeLabel:
    'Квестовая часть: около 20–30 минут. Паспорт миссии и короткая проверка с родителем: около 35–50 минут. / Quest part: about 20–30 minutes. Mission passport and short parent check: about 35–50 minutes.',
  nimnyIntro:
    'Третий огонёк спрятан на мосту между островами. Чтобы его зажечь, Нимни нужно вспомнить измерения, время, расстояние, карту и маршрут. / The third light is hidden on the bridge between islands. To light it, remember measurement, time, distance, maps and routes.',
  goals: [
    'Огонёк знаний № 3: измерения, карты и маршрут. / Light of knowledge #3: measurements, maps and route.',
    'Слой 1. Математика / Layer 1. Math: расстояние, время, шаги, периметр, простой масштаб (Grade 5 / middle).',
    'Слой 2. Русский язык / Layer 2. Russian: словарь огонька мира и мини-диктант.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Как карта и измерения помогают не заблудиться?».',
    'Слой 4. Карточки карты / Layer 4. Map cards: карта, маршрут, масштаб, измерение, компас, расписание.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: выбор маршрута и рисунок огонька. / Route choice and the third light drawing.',
    'Дневник мышления / Thinking Diary.',
    'Награда нимами / Reward in nims: 3/5.'
  ],
  vocabulary: [
    { term: 'измерение / measurement', definition: 'проверка числами / a check with numbers' },
    { term: 'длина / length', definition: 'насколько что-то долго / how long something is' },
    { term: 'время / time', definition: 'часы и минуты / hours and minutes' },
    { term: 'расстояние / distance', definition: 'насколько далеко / how far' },
    { term: 'маршрут / route', definition: 'путь от старта к финишу / a path from start to finish' },
    { term: 'карта / map', definition: 'вид мест сверху / view of places from above' },
    { term: 'масштаб / scale', definition: 'связь карты и реальности / link between map and real distance' },
    { term: 'направление / direction', definition: 'куда идти / which way to go' },
    { term: 'север / north', definition: 'верх компаса / top of the compass' },
    { term: 'юг / south', definition: 'низ компаса / bottom of the compass' }
  ],
  onlineTasks: [
    { id: 'q1', type: 'math', title: 'Длина маршрута / Route distance',
      prompt: 'Маршрут из 3 частей: 120 м + 80 м + 100 м. Сколько всего? / Route 3 parts: 120 + 80 + 100 m. Total?',
      helper: '120 + 80 + 100. / Sum.', answer: '300', unit: 'метров / meters' },
    { id: 'q2', type: 'math', title: 'Когда Нимни закончит? / When will Nimny finish?',
      prompt: 'Старт в 10:20, идёт 35 минут. Во сколько закончит? / Start 10:20, walks 35 minutes. Finish time?',
      helper: '10:20 + 35 мин. / 10:20 + 35 min.', answer: '10:55' },
    { id: 'q3', type: 'math', title: 'Шаги по карте / Steps on the map',
      prompt: '1 шаг = 10 м. Нимни 4 шага на восток и 3 шага на север. Сколько всего шагов? Сколько метров? / 1 step = 10 m. 4 east + 3 north. Steps total? Meters?',
      helper: '4 + 3 шагов; × 10 м. / 4 + 3 steps; × 10 m.', answer: '4 + 3 = 7 шагов; 7 × 10 = 70 метров. / 7 steps; 70 m.' },
    { id: 'q4', type: 'math', title: 'Периметр сада / Garden perimeter',
      prompt: 'Сад 12 м × 8 м. Какой периметр? / Garden 12 × 8 m. Perimeter?',
      helper: '2 × (12 + 8). / 2 × (12 + 8).', answer: '40', unit: 'метров / meters' },
    { id: 'q5', type: 'math', title: 'Масштаб карты / Map scale',
      prompt: 'На карте 1 см = 100 м. Маршрут на карте 3 см. Сколько в реальности? / Map 1 cm = 100 m. Route 3 cm. Real distance?',
      helper: '3 × 100. / 3 × 100.', answer: '300', unit: 'метров / meters' },
    { id: 'q6', type: 'classify', title: 'Карточки карты / Map cards',
      prompt: 'Подбери определение каждому слову. / Match each word with a definition.',
      categories: [
        { id: 'map', label: 'Карта / Map' },
        { id: 'route', label: 'Маршрут / Route' },
        { id: 'scale', label: 'Масштаб / Scale' },
        { id: 'measure', label: 'Измерение / Measurement' },
        { id: 'compass', label: 'Компас / Compass' },
        { id: 'schedule', label: 'Расписание / Schedule' }
      ],
      items: [
        { id: 'd_map', text: 'показывает места сверху / shows places from above' },
        { id: 'd_route', text: 'путь от старта к финишу / path from start to finish' },
        { id: 'd_scale', text: 'связывает расстояние на карте с реальностью / links map distance with real distance' },
        { id: 'd_measure', text: 'проверка числами / checking with numbers' },
        { id: 'd_compass', text: 'помогает с направлением / helps with directions' },
        { id: 'd_schedule', text: 'помогает со временем / helps with time' }
      ] },
    { id: 'q7', type: 'classify', title: 'Какой маршрут выбрать? / Which route to choose?',
      prompt: 'Распредели маршруты. / Sort the routes.',
      categories: [
        { id: 'short_slow', label: 'Короткий, но медленный / Short but slow (A)' },
        { id: 'long_easy', label: 'Длинный, но лёгкий / Long but easy (B)' },
        { id: 'check', label: 'Нужно проверить / Needs checking (C)' }
      ],
      items: [
        { id: 'r_a', text: 'Маршрут A: 200 м, через лестницу. / Route A: 200 m, through stairs.' },
        { id: 'r_b', text: 'Маршрут B: 350 м, ровная дорога. / Route B: 350 m, flat road.' },
        { id: 'r_c', text: 'Маршрут C: расстояние неизвестно, надо посмотреть карту. / Route C: distance unknown, check the map.' }
      ] },
    { id: 'q8', type: 'text', title: 'Исправь ошибки / Fix the mistakes',
      prompt: 'Перепиши без ошибок и расставь знаки: «карта памагает найти путь измерения помагают праверить дагатку». / Rewrite without mistakes (Russian sentence above).',
      placeholder: 'Карта помогает найти путь. / A map helps find the way.' },
    { id: 'q9', type: 'essay', title: 'Как карта и измерения помогают не заблудиться? / How do maps and measurements help us not get lost?',
      prompt: 'Напиши 3–5 предложений по-русски. Используй слова: карта, маршрут, масштаб, измерение, компас. / Write 3–5 Russian sentences using the keywords.',
      minSentences: 3, maxSentences: 5, placeholder: 'Карта и измерения помогают мне, потому что... / Maps and measurements help me because...' },
    { id: 'q10', type: 'essay', title: 'Тихий вопрос Нимни / A quiet question from Nimny',
      prompt: 'Напиши 2–4 предложения: какой путь ты выбрал и почему? Используй слово «огонёк». / Write 2–4 sentences about your route choice; use the word "огонёк".',
      minSentences: 2, maxSentences: 4, placeholder: 'Я выбрал маршрут... потому что... / I chose route... because...' }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 10, День 3 — Огонёк мира». / Heading: "Week 10, Day 3 — World Light".',
    'Слой 1. Математика / Layer 1. Math: q1–q5 + опц. сравнение двух маршрутов. / + optional route comparison.',
    'Слой 2. Русский язык / Layer 2. Russian: словарь и мини-диктант: «Карта помогает найти путь. Измерения помогают проверить догадку.» + правка. / vocabulary, mini-dictation, edit.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Как карта и измерения помогают не заблудиться?». / 3–5 sentences.',
    'Слой 4. Карточки карты / Layer 4. Map cards: 6 терминов с определениями. / 6 terms with definitions.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: выбор маршрута и рисунок огонька мира. / Route choice and the world-light drawing.',
    'Дневник мышления / Thinking diary.',
    'Награда нимами после проверки. / Reward in nims after parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 10, День 3 / Week 10, Day 3',
    subtitle: 'Огонёк мира: измерения, карты и маршрут / World Light: Measurements, Maps and Route',
    pages: [
      { title: 'Маршрут миссии / Mission route', blocks: [
        { type: 'text', text: 'Поставь штамп ✦, когда выполнено. / Stamp ✦ when done.' },
        { type: 'list', items: [
          '✦ 1. Быстрая математика / Quick math.',
          '✦ 2. Русский язык — слова и мини-диктант / Russian — words and mini-dictation.',
          '✦ 3. Короткое письмо «Как карта и измерения помогают?» / Short writing.',
          '✦ 4. Карточки карты / Map cards.',
          '✦ 5. Выбор маршрута и рисунок огонька / Route choice and light drawing.',
          '✦ 6. Дневник мышления / Thinking diary.',
          '✦ 7. Проверка с родителем / Parent check.',
          '✦ 8. Печать миссии и нимы / Mission stamp and nims.'
        ] }
      ] },
      { title: 'Слой 1. Математика — быстрая миссия / Layer 1. Math — Quick mission', blocks: [
        { type: 'list', items: [
          '1) 120 + 80 + 100 = ____ метров маршрута / route meters.',
          '2) 10:20 + 35 мин = ____ время финиша / finish time.',
          '3) 4 + 3 = ____ шагов; × 10 м = ____ метров (карта 1 шаг = 10 м) / steps and meters.',
          '4) 2 × (12 + 8) = ____ метров периметра сада 12×8 / garden perimeter.',
          '5) 3 см × 100 м/см = ____ метров реальности / real distance.',
          '6) Optional / Доп.: маршрут A 200 м со ступенями vs маршрут B 350 м ровный — какой быстрее? Какой легче? / Faster? Easier?'
        ] }
      ] },
      { title: 'Слой 2. Русский язык — слова и мини-диктант / Layer 2. Russian — Words and Mini-Dictation', blocks: [
        { type: 'text', text: 'Лёгкое повторение: словарь огонька мира. / Light review: world-light vocabulary.' },
        { type: 'list', items: [
          'Словарь / Vocabulary: измерение, длина, время, расстояние, маршрут, карта, масштаб, направление, север, юг.',
          'Мини-диктант / Mini-dictation: «Карта помогает найти путь. Измерения помогают проверить догадку.»',
          'Исправь ошибки / Fix mistakes: «карта памагает найти путь измерения помагают праверить дагатку».',
          'Орфограмма дня / Spelling rule of the day: безударные гласные и пунктуация. / Unstressed vowels and punctuation.'
        ] }
      ] },
      { title: 'Слой 3. Чтение и короткое письмо / Layer 3. Reading and Short Writing', blocks: [
        { type: 'list', items: [
          'Прочитай ещё раз слова огонька мира. / Re-read the world-light vocabulary.',
          'Напиши 3–5 предложений «Как карта и измерения помогают не заблудиться?» / Write 3–5 Russian sentences "How do maps and measurements help us not get lost?": ____________________________________________',
          'Используй слова: карта, маршрут, масштаб, измерение, компас / Use the keywords.'
        ] }
      ] },
      { title: 'Слой 4. Карточки карты и сортировка / Layer 4. Map Cards and Sorting', blocks: [
        { type: 'text', text: 'Подбери определение каждому слову (соедини стрелкой). / Match each word with its definition (draw an arrow).' },
        { type: 'list', items: [
          'карта → ____',
          'маршрут → ____',
          'масштаб → ____',
          'измерение → ____',
          'компас → ____',
          'расписание → ____',
          'Варианты / Options: показывает места сверху · путь от старта к финишу · связывает карту с реальностью · проверка числами · помогает с направлением · помогает со временем.'
        ] }
      ] },
      { title: 'Слой 5. Огонёк знаний — выбор маршрута и рисунок / Layer 5. Knowledge Light — Route Choice and Drawing (practical)', blocks: [
        { type: 'text', text: 'Выбери маршрут A, B или C и нарисуй третий огонёк. / Choose route A, B or C and draw the third light.' },
        { type: 'list', items: [
          'Маршрут A — короткий, но медленный (200 м, ступени) / Short but slow ▢',
          'Маршрут B — длинный, но лёгкий (350 м, ровно) / Long but easy ▢',
          'Маршрут C — нужно проверить (расстояние неизвестно) / Needs checking ▢',
          'Почему я выбрал именно так / Why I chose this way: ____________________________________________',
          'Рисунок огонька мира / World-light drawing: ▢▢▢',
          'Подпись / Caption: «Я умею находить путь.»'
        ] }
      ] },
      { title: 'Дневник мышления / Thinking Diary', blocks: [
        { type: 'list', items: [
          'Что было самым весёлым в третьей миссии? / Most fun in mission #3: ___________________',
          'Что было самым важным? / Most important: ___________________'
        ] }
      ] },
      { title: 'Родительская проверка, печать миссии и награда нимами / Parent check, mission stamp and reward in nims', blocks: [
        { type: 'list', items: [
          'Печать миссии / Mission stamp: ▢ ✦ ОГОНЁК МИРА ЗАЖЖЁН ✦ / WORLD LIGHT IS LIT.',
          'Базовая награда / Base reward: 3 нима / nims ✓',
          'Бонус за точный расчёт маршрута / Bonus for accurate route math: +1 ☐',
          'Бонус за ясное объяснение пути / Bonus for a clear route explanation: +1 ☐',
          'Итого / Total: ____ из / of 5 нимов / nims'
        ] }
      ] }
    ]
  },
  parentGuide: {
    goal:
      'Лёгкая квестовая миссия: повторить идеи Недель 3–4 (измерения, время, маршрут, карта, простой масштаб). Самостоятельная работа; роль родителя — короткое введение (3–5 минут), помощь только если ребёнок застрял, и финальная проверка (5–10 минут). Не экзамен. / Light quest mission reviewing Weeks 3–4. Child-led; parent gives 3–5 min intro, helps only if stuck, 5–10 min final check. Not a test.',
    safetyNote: 'Только учебная симуляция; никаких самостоятельных прогулок без взрослого, никакого ввода реальных адресов или геоданных. / Educational simulation only; no independent walks without an adult, no real addresses or geolocation entry.',
    mathGoal: 'q1: 120+80+100 = 300 м. q2: 10:20+35 = 10:55. q3: 4+3 = 7 шагов; 7×10 = 70 м. q4: 2×(12+8) = 40 м. q5: 3×100 = 300 м. Опц.: A быстрее, B легче. / Listed.',
    russianGoal: 'Словарь огонька мира запомнен; мини-диктант чистый; правка корректна. / Vocabulary recalled; clean dictation; edit correct.',
    readingWritingGoal: '3–5 предложений по-русски с минимум 3 ключевыми словами. / 3–5 Russian sentences using ≥ 3 keywords.',
    missionGoal: '6 терминов карты подобраны корректно. / 6 map terms matched correctly.',
    practicalGoal: 'Один маршрут выбран и обоснован; рисунок огонька подписан «Я умею находить путь». / One route chosen and justified; light drawing signed.',
    check: [
      'Q1: 300 м / 300 m.',
      'Q2: 10:55 / 10:55.',
      'Q3: 7 шагов = 70 м / 7 steps = 70 m.',
      'Q4: 40 м / 40 m.',
      'Q5: 300 м / 300 m.',
      'Карточки карты подобраны / Map cards matched.',
      'Маршрут выбран и обоснован / Route chosen and justified.',
      '3–5 предложений «Как карта и измерения помогают?» / 3–5 sentences.'
    ],
    answerKey: [
      { taskId: 'q1', answer: '120 + 80 + 100 = 300 метров. / 300 m.' },
      { taskId: 'q2', answer: '10:20 + 35 минут = 10:55. / 10:55.' },
      { taskId: 'q3', answer: '4 + 3 = 7 шагов; 7 × 10 = 70 метров. / 7 steps; 70 m.' },
      { taskId: 'q4', answer: '2 × (12 + 8) = 40 метров. / 40 m.' },
      { taskId: 'q5', answer: '3 × 100 = 300 метров. / 300 m.' },
      { taskId: 'q6', answer: 'карта — показывает места сверху; маршрут — путь от старта к финишу; масштаб — связывает карту с реальностью; измерение — проверка числами; компас — помогает с направлением; расписание — помогает со временем. / Listed.' },
      { taskId: 'q7', answer: 'Маршрут A — короткий, но медленный (200 м, ступени). Маршрут B — длинный, но лёгкий (350 м, ровно). Маршрут C — нужно проверить (расстояние неизвестно). / Listed.' },
      { taskId: 'q8', answer: 'Карта помогает найти путь. Измерения помогают проверить догадку.' },
      { taskId: 'q9', answer: 'Открытый ответ. 3–5 предложений по-русски с минимум 3 ключевыми словами и идеей: карта показывает путь, измерения проверяют расстояние и время. / Open answer.' },
      { taskId: 'q10', answer: 'Открытый ответ. 2–4 предложения; выбор маршрута обоснован; слово «огонёк» использовано. / Open answer.' }
    ],
    spellingCheck: 'Словарь и диктант чистые; правка корректна. / Vocabulary recalled; clean dictation; edit correct.',
    readingCheck: '3–5 предложений по-русски; ключевые слова использованы. / 3–5 Russian sentences with keywords.',
    missionCheck: '6 терминов карты подобраны без явных ошибок. / 6 map terms matched without clear errors.',
    notebookCheck: 'Дневник мышления заполнен; рисунок огонька подписан. / Diary filled; light drawing signed.',
    printCheck: 'Все 8 страниц паспорта миссии заполнены. / All 8 mission-passport pages completed.',
    discussionPrompts: [
      'Что такое маршрут? / What is a route?',
      'Что такое карта? / What is a map?',
      'Зачем мы измеряем? / Why do we measure?',
      'Какой маршрут ты выбрал и почему? / Which route did you choose and why?'
    ],
    parentRoleNote: 'Введение 3–5 минут, помощь только если застрял, финальная проверка 5–10 минут и начисление нимов. Не экзамен. / 3–5 min intro, help only if stuck, 5–10 min final check, then award nims. Not a test.',
    ifEasy: 'Усложните: пусть ребёнок построит свой маршрут на карте 1 см = 100 м длиной 4 см и нарисует его. / Make harder: build a 4 cm route at 1 cm = 100 m and draw it.',
    ifDifficult: 'Сократите: оставьте q1, q2 и рисунок огонька. / Make easier: keep q1, q2 and the light drawing.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию / Mission reward', display: '3 нима / 3 nims',
    bonuses: [
      { id: 'route-math', label: 'Бонус за точный расчёт маршрута / Bonus for accurate route math', amount: 1 },
      { id: 'clear-route-explanation', label: 'Бонус за ясное объяснение пути / Bonus for a clear route explanation', amount: 1 }
    ]
  },
  completionRequirements: { requiredTaskIds: ['q1', 'q6', 'q7', 'q9', 'q10'], allowNotebookCompletion: true },
  nextLessonId: 'max-week-10-day-4'
};

export default maxWeek10Day3;
